import type { TrackerKpi, Week } from 'chery-contracts';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { cn } from '../../lib/cn';
import { PILLAR_CLASSES, PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { cellText, coveredWindowLabel, weekDateLabel } from './tracker-display';
import type { CellRecord } from './tracker-state';

export interface CommentPanelProps {
  kpi: TrackerKpi;
  week: Week;
  record: CellRecord;
  onApply: (notes: { comment: string | null; reason: string | null; followUp: string | null }) => void;
  onClearValue: () => void;
  onClose: () => void;
}

const orNull = (text: string): string | null => (text.trim() === '' ? null : text.trim());

/**
 * The notes of one cell (plan §3.1): weekly comment, reason for movement, follow-up. Applying stages them like any
 * other edit (Save commits, Undo reverts). A note lives with its value — clearing the value removes the row and its
 * notes — so a blank cell cannot take notes. Read-only for a pillar the user may only view: plain text, no inputs.
 */
export const CommentPanel = ({ kpi, week, record, onApply, onClearValue, onClose }: CommentPanelProps) => {
  const { t } = useI18nTranslation();
  const [comment, setComment] = useState(record.comment ?? '');
  const [reason, setReason] = useState(record.reason ?? '');
  const [followUp, setFollowUp] = useState(record.followUp ?? '');

  useEffect(() => {
    setComment(record.comment ?? '');
    setReason(record.reason ?? '');
    setFollowUp(record.followUp ?? '');
  }, [kpi.id, week.id, record.comment, record.reason, record.followUp]);

  const hasValue = record.value !== null;
  const canEdit = kpi.editable && hasValue;
  const changed = orNull(comment) !== record.comment || orNull(reason) !== record.reason || orNull(followUp) !== record.followUp;

  const field = (id: string, label: string, value: string, onChange: (next: string) => void, multiline = false) => (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="text-xs font-medium text-muted-foreground"
      >
        {label}
      </label>
      {kpi.editable ? (
        multiline ? (
          <Textarea
            id={id}
            rows={4}
            value={value}
            disabled={!canEdit}
            onChange={(event) => onChange(event.target.value)}
          />
        ) : (
          <Input
            id={id}
            value={value}
            disabled={!canEdit}
            onChange={(event) => onChange(event.target.value)}
          />
        )
      ) : (
        <p
          id={id}
          className="min-h-[1.25rem] whitespace-pre-wrap text-sm text-foreground"
        >
          {value || '—'}
        </p>
      )}
    </div>
  );

  return (
    <aside
      aria-label={t('tracker.comments.title')}
      className="flex w-80 shrink-0 flex-col border-s border-border bg-card"
    >
      <header className="flex items-start gap-3 border-b border-border p-4">
        <div className="min-w-0 flex-1">
          <p className={cn('text-[11px] font-semibold uppercase tracking-wide', PILLAR_CLASSES[kpi.pillar].text)}>{PILLAR_META[kpi.pillar].label}</p>
          <p className="mt-0.5 text-sm font-semibold text-card-foreground">{kpi.name}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('actions.close')}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      <dl className="grid grid-cols-2 gap-3 border-b border-border p-4 text-xs">
        <div>
          <dt className="text-muted-foreground">{t('tracker.comments.week')}</dt>
          <dd className="mt-0.5 font-mono font-semibold text-foreground">{weekDateLabel(week.weekDate)}</dd>
          <dd className="text-[11px] text-muted-foreground">{t('tracker.comments.covers', { window: coveredWindowLabel(week.weekDate) })}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('tracker.comments.value')}</dt>
          <dd className="mt-0.5 font-mono font-semibold text-foreground">{cellText(kpi, record.value)}</dd>
        </div>
      </dl>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {field('tracker-comment', t('tracker.comments.comment'), comment, setComment, true)}
        {field('tracker-reason', t('tracker.comments.reason'), reason, setReason)}
        {field('tracker-follow-up', t('tracker.comments.followUp'), followUp, setFollowUp)}
        <p className="text-xs text-muted-foreground">
          {!kpi.editable ? t('tracker.comments.readOnly') : hasValue ? t('tracker.comments.hint') : t('tracker.comments.needsValue')}
        </p>
      </div>

      {kpi.editable ? (
        <footer className="flex gap-2 border-t border-border p-4">
          <Button
            className="flex-1"
            size="sm"
            disabled={!canEdit || !changed}
            onClick={() => onApply({ comment: orNull(comment), reason: orNull(reason), followUp: orNull(followUp) })}
          >
            {t('tracker.comments.apply')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasValue}
            onClick={onClearValue}
          >
            {t('tracker.comments.clearValue')}
          </Button>
        </footer>
      ) : null}
    </aside>
  );
};
